import { Component, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { ApiService } from '../../../services/api.service';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';
import { extractData, extractMessage, extractErrorMessage, ApiResponse } from '../../../helpers/api-response.helper';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-view-lead-details',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule, ConfirmationModalComponent],
  templateUrl: './view-lead-details.component.html',
  styleUrls: ['./view-lead-details.component.css']
})
export class ViewLeadDetailsComponent implements OnInit {
  lead: any = null;
  product: any = null;
  loading = false;
  errorMessage = '';
  successMessage = '';
  activeTab: 'specification' | 'quote' = 'specification';
  
  // Quote form
  quoteForm!: FormGroup;
  submitting = false;
  quantitiesInitialized = false;
  isFromLeads = false; // Track if coming from leads section
  
  // Confirmation modal state
  showConfirmModal = false;
  confirmModalMessage = '';
  
  // Image modal state
  showImageModal = false;
  modalImageUrl = '';
  modalImageName = '';
  isSupplier = false;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private authService: AuthService,
  ) {
    this.quoteForm = this.fb.group({
      validityDate: [''], // Start blank - will add required validator only when editable
      quantityPrices: this.fb.array([]),
      notes: ['']
    });
    this.isSupplier = this.authService.isSupplier();
    console.log(this.isSupplier,"this.isSupplier")
  }

  ngOnInit() {
    const leadId = this.route.snapshot.paramMap.get('id');
    // Check if coming from leads section or quotes section
    const currentRoute = this.router.url;
    this.isFromLeads = currentRoute.startsWith('/leads/view');
    
    if (leadId) {
      this.loadLeadDetails(leadId);
    }
  }

  loadLeadDetails(leadId: string) {
    this.loading = true;
    this.errorMessage = '';
    this.quantitiesInitialized = false; // Reset flag when loading new data
    
    this.apiService.getQuote(leadId).subscribe({
      next: (response: ApiResponse) => {
        // Extract data from standardized response
        const quoteData = extractData(response);
        this.lead = quoteData;
        
        // Debug: Log the lead status
        console.log('Lead response:', quoteData);
        console.log('Lead loaded:', {
          status: quoteData?.status,
          lead: quoteData
        });
        
        // Use product from response, or fallback to productDetailsSnapshot or productDetails
        this.product = quoteData?.product || quoteData?.productDetailsSnapshot || quoteData?.productDetails || {};
        
        // Ensure quantities are available - check all possible sources in priority order
        if (!this.product.quantities || this.product.quantities.length === 0) {
          // Check productDetails first
          if (quoteData?.productDetails?.quantities && Array.isArray(quoteData.productDetails.quantities) && quoteData.productDetails.quantities.length > 0) {
            this.product.quantities = quoteData.productDetails.quantities;
          } 
          // Then check productDetailsSnapshot
          else if (quoteData?.productDetailsSnapshot?.quantities && Array.isArray(quoteData.productDetailsSnapshot.quantities) && quoteData.productDetailsSnapshot.quantities.length > 0) {
            this.product.quantities = quoteData.productDetailsSnapshot.quantities;
          } 
          // Then check product.quantities directly
          else if (quoteData?.product?.quantities && Array.isArray(quoteData.product.quantities) && quoteData.product.quantities.length > 0) {
            this.product.quantities = quoteData.product.quantities;
          }
        }
        
        // Also store productDetails separately for easy access
        if (quoteData?.productDetails) {
          if (!this.product.productDetails) {
            this.product.productDetails = quoteData.productDetails;
          }
          // Ensure quantities are in productDetails
          if (!this.product.productDetails.quantities && this.product.quantities) {
            this.product.productDetails.quantities = this.product.quantities;
          }
        }
        
        // Also store lead's productDetails for access
        if (quoteData?.productDetails) {
          this.lead.productDetails = quoteData.productDetails;
          // Ensure quantities are in lead.productDetails
          if (!this.lead.productDetails.quantities && this.product.quantities) {
            this.lead.productDetails.quantities = this.product.quantities;
          }
        }
        
        // Also store productDetailsSnapshot in lead
        if (quoteData?.productDetailsSnapshot) {
          this.lead.productDetailsSnapshot = quoteData.productDetailsSnapshot;
          // Ensure quantities are in productDetailsSnapshot
          if (!this.lead.productDetailsSnapshot.quantities && this.product.quantities) {
            this.lead.productDetailsSnapshot.quantities = this.product.quantities;
          }
        }
        
        // If product doesn't have category but quote has category, use quote's category
        if (this.product && !this.product.category && quoteData?.category) {
          this.product.category = quoteData.category;
        }
        
        // If product doesn't have createdAt, try to get it from productDetailsSnapshot or use quote's createdAt
        if (this.product && !this.product.createdAt) {
          if (quoteData?.productDetailsSnapshot?.createdAt) {
            this.product.createdAt = quoteData.productDetailsSnapshot.createdAt;
          } else if (quoteData?.productDetails?.createdAt) {
            this.product.createdAt = quoteData.productDetails.createdAt;
          } else if (quoteData?.createdAt) {
            this.product.createdAt = quoteData.createdAt;
          }
        }
        
        // Debug: Log quantities
        console.log('Product quantities:', {
          product: this.product,
          productQuantities: this.product?.quantities,
          productDetailsQuantities: quoteData?.productDetails?.quantities,
          productDetailsSnapshotQuantities: quoteData?.productDetailsSnapshot?.quantities,
          leadProductDetailsSnapshotQuantities: this.lead?.productDetailsSnapshot?.quantities
        });
        
        // Initialize quote form with existing data
        // Only set validityDate if quote is editable (Yet to Quote status)
        const isEditable = (quoteData?.status || '').trim() === 'Yet to Quote';
        
        if (isEditable) {
          // Only set validityDate if it already exists (supplier has previously filled it)
          // Otherwise, leave it blank for supplier to fill
          if (quoteData?.validityDate) {
            this.quoteForm.patchValue({
              validityDate: this.formatDateForInput(quoteData.validityDate),
              notes: quoteData.notes || ''
            });
          } else {
            // Leave validityDate blank - supplier must fill it
            this.quoteForm.patchValue({
              validityDate: '',
              notes: quoteData.notes || ''
            });
          }
          
          // Enable validityDate field for editing and set required validator
          const validityDateControl = this.quoteForm.get('validityDate');
          validityDateControl?.enable();
          validityDateControl?.setValidators([Validators.required]);
          validityDateControl?.updateValueAndValidity();
        } else {
          // Disable validityDate field if quote is not editable
          const validityDateControl = this.quoteForm.get('validityDate');
          validityDateControl?.disable();
          validityDateControl?.clearValidators();
          validityDateControl?.updateValueAndValidity();
          
          if (quoteData?.validityDate) {
            this.quoteForm.patchValue({
              validityDate: this.formatDateForInput(quoteData.validityDate),
              notes: quoteData.notes || ''
            });
          } else {
            this.quoteForm.patchValue({
              notes: quoteData.notes || ''
            });
          }
        }
        
        // Initialize quantity prices immediately
        this.initializeQuantityPrices();
        this.getStatusClass();
        this.getStatusText();
        this.loading = false;
        
        // Force change detection after all initialization is complete
        this.ngZone.run(() => {
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        });
        
        // One more change detection after a brief delay to ensure everything is settled
        setTimeout(() => {
          this.ngZone.run(() => {
            this.cdr.markForCheck();
            this.cdr.detectChanges();
          });
        }, 50);
      },
      error: (err: ApiResponse) => {
        console.error('Error loading lead details:', err);
        const errorMessage = extractErrorMessage(err) || 'Failed to load lead details';
        this.errorMessage = errorMessage;
        this.loading = false;
      }
    });
  }

  initializeQuantityPrices() {
    // Prevent multiple initializations
    if (this.quantitiesInitialized) {
      console.log('Quantities already initialized, skipping...');
      return;
    }
    
    // Get quantities from multiple sources - check in order of priority
    let quantities: any[] = [];
    
    console.log('initializeQuantityPrices - Checking all sources:', {
      product: this.product,
      lead: this.lead,
      productQuantities: this.product?.quantities,
      leadProductDetails: this.lead?.productDetails,
      leadProductDetailsQuantities: this.lead?.productDetails?.quantities,
      productProductDetails: this.product?.productDetails,
      productProductDetailsQuantities: this.product?.productDetails?.quantities,
      leadProductDetailsSnapshot: this.lead?.productDetailsSnapshot,
      leadProductDetailsSnapshotQuantities: this.lead?.productDetailsSnapshot?.quantities
    });
    
    // First priority: product.quantities
    if (this.product?.quantities && Array.isArray(this.product.quantities) && this.product.quantities.length > 0) {
      quantities = this.product.quantities;
      console.log('Using product.quantities:', quantities);
    }
    // Second priority: lead.productDetails.quantities
    else if (this.lead?.productDetails?.quantities && Array.isArray(this.lead.productDetails.quantities) && this.lead.productDetails.quantities.length > 0) {
      quantities = this.lead.productDetails.quantities;
      console.log('Using lead.productDetails.quantities:', quantities);
    }
    // Third priority: product.productDetails.quantities
    else if (this.product?.productDetails?.quantities && Array.isArray(this.product.productDetails.quantities) && this.product.productDetails.quantities.length > 0) {
      quantities = this.product.productDetails.quantities;
      console.log('Using product.productDetails.quantities:', quantities);
    }
    // Fourth priority: lead.productDetailsSnapshot.quantities
    else if (this.lead?.productDetailsSnapshot?.quantities && Array.isArray(this.lead.productDetailsSnapshot.quantities) && this.lead.productDetailsSnapshot.quantities.length > 0) {
      quantities = this.lead.productDetailsSnapshot.quantities;
      console.log('Using lead.productDetailsSnapshot.quantities:', quantities);
    }
    // Fifth priority: product.productDetailsSnapshot.quantities
    else if (this.product?.productDetailsSnapshot?.quantities && Array.isArray(this.product.productDetailsSnapshot.quantities) && this.product.productDetailsSnapshot.quantities.length > 0) {
      quantities = this.product.productDetailsSnapshot.quantities;
      console.log('Using product.productDetailsSnapshot.quantities:', quantities);
    }
    
    console.log('initializeQuantityPrices - Final quantities array:', quantities);
    
    const existingPrices = this.lead?.quantityPrices || [];
    
    const quantityPricesArray = this.quoteForm.get('quantityPrices') as FormArray;
    if (!quantityPricesArray) {
      console.error('quantityPrices FormArray not found');
      this.quantitiesInitialized = true; // Mark as initialized to prevent retries
      return;
    }
    
    // Clear existing controls properly - remove from end to avoid index issues
    const currentLength = quantityPricesArray.length;
    for (let i = currentLength - 1; i >= 0; i--) {
      quantityPricesArray.removeAt(i);
    }
    
    const isEditable = this.isQuoteEditable();
    
    if (!quantities || quantities.length === 0) {
      console.warn('No quantities found in product or lead data');
      // Mark as initialized even if no quantities to prevent retries
      this.quantitiesInitialized = true;
      // Force change detection even if no quantities
      this.cdr.detectChanges();
      return;
    }
    
    console.log('Initializing quantities, count:', quantities.length);
    
    quantities.forEach((qty: any, index: number) => {
      // Ensure quantity and uom exist - handle both number and string quantity
      const quantityValue = typeof qty.quantity === 'string' ? parseFloat(qty.quantity) : qty.quantity;
      const uomValue = qty.uom || '';
      
      if (quantityValue === undefined || quantityValue === null || isNaN(quantityValue) || !uomValue) {
        console.warn('Invalid quantity item:', qty, 'quantityValue:', quantityValue, 'uomValue:', uomValue);
        return;
      }
      
      const existingPrice = existingPrices.find((p: any) => {
        const pQuantity = typeof p.quantity === 'string' ? parseFloat(p.quantity) : p.quantity;
        return pQuantity === quantityValue && p.uom === uomValue;
      });
      
      // Check if this quantity is mandatory - handle boolean, string, and undefined/null cases
      let isMandatory = false;
      if (qty.isMandatory !== undefined && qty.isMandatory !== null) {
        if (typeof qty.isMandatory === 'boolean') {
          isMandatory = qty.isMandatory === true;
        } else if (typeof qty.isMandatory === 'string') {
          isMandatory = qty.isMandatory.toLowerCase() === 'true';
        }
      }
      
      console.log('Quantity item:', { quantity: quantityValue, uom: uomValue, isMandatory: qty.isMandatory, parsedIsMandatory: isMandatory });
      
      // Set validators: required only if mandatory and editable
      const unitPriceValidators = [];
      if (isEditable && isMandatory) {
        unitPriceValidators.push(Validators.required, Validators.min(0.01));
      } else if (isEditable) {
        // Optional but if provided, must be > 0
        unitPriceValidators.push(Validators.min(0.01));
      }
      
      const priceGroup = this.fb.group({
        quantity: [quantityValue],
        uom: [uomValue],
        isMandatory: [isMandatory], // Store mandatory flag as boolean
        unitPrice: [{ value: existingPrice?.unitPrice || null, disabled: !isEditable }, unitPriceValidators],
        totalPrice: [{ value: existingPrice?.totalPrice || 0, disabled: true }]
      });
      
      // Calculate total when unit price changes (always, not just when editable)
      const unitPriceControl = priceGroup.get('unitPrice');
      if (unitPriceControl) {
        // Subscribe to value changes for reactive updates
        unitPriceControl.valueChanges.subscribe((newValue) => {
          // Calculate immediately when value changes
          setTimeout(() => {
            this.calculateTotalPrice(index);
          }, 0);
        });
        
        // Calculate initial total if unitPrice already has a value
        const initialUnitPrice = unitPriceControl.value;
        if (initialUnitPrice && initialUnitPrice > 0) {
          setTimeout(() => {
            this.calculateTotalPrice(index);
          }, 50);
        }
      }
      
      // Add the form group to the array
      quantityPricesArray.push(priceGroup);
      console.log('Added quantity to form array:', { quantity: quantityValue, uom: uomValue, index, arrayLength: quantityPricesArray.length });
    });
    
    console.log('initializeQuantityPrices - form array length:', quantityPricesArray.length);
    console.log('initializeQuantityPrices - controls:', quantityPricesArray.controls);
    
    // Update form array validity to ensure all controls are registered
    quantityPricesArray.updateValueAndValidity({ emitEvent: false });
    
    // Update the parent form to ensure all controls are registered
    this.quoteForm.updateValueAndValidity({ emitEvent: false });
    
    // Mark as initialized AFTER all controls are added and validated
    this.quantitiesInitialized = true;
    
    // Run change detection inside Angular zone to ensure it's properly detected
    this.ngZone.run(() => {
      // Force change detection after populating the FormArray
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    });
    
    // Additional change detection after a brief delay to ensure template binding
    setTimeout(() => {
      this.ngZone.run(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });
    }, 50);
  }

  get quantityPrices(): FormArray {
    const formArray = this.quoteForm.get('quantityPrices') as FormArray;
    console.log('quantityPrices getter called - length:', formArray?.controls?.length || 0);
    return formArray;
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  calculateTotalPrice(index: number) {
    try {
      const priceGroup = this.quantityPrices.at(index);
      if (!priceGroup) {
        console.warn('Price group not found at index:', index);
        return;
      }
      
      // Get current values from form controls
      const quantityControl = priceGroup.get('quantity');
      const unitPriceControl = priceGroup.get('unitPrice');
      const totalPriceControl = priceGroup.get('totalPrice');
      
      if (!quantityControl || !unitPriceControl || !totalPriceControl) {
        console.warn('Form controls not found at index:', index);
        return;
      }
      
      const quantity = quantityControl.value;
      const unitPrice = unitPriceControl.value;
      
      // Convert to numbers, handle all edge cases
      let quantityNum = 0;
      let unitPriceNum = 0;
      
      if (quantity !== null && quantity !== undefined && quantity !== '') {
        quantityNum = typeof quantity === 'string' ? parseFloat(quantity.replace(/,/g, '')) : Number(quantity);
      }
      
      if (unitPrice !== null && unitPrice !== undefined && unitPrice !== '') {
        unitPriceNum = typeof unitPrice === 'string' ? parseFloat(unitPrice.replace(/,/g, '')) : Number(unitPrice);
      }
      
      // Check for NaN
      if (isNaN(quantityNum)) quantityNum = 0;
      if (isNaN(unitPriceNum)) unitPriceNum = 0;
      
      // Calculate total: quantity * unitPrice
      const totalPrice = quantityNum * unitPriceNum;
      
      // Update total price control directly
      totalPriceControl.setValue(totalPrice, { emitEvent: false, onlySelf: false });
      
      // Mark the control as touched to ensure it's considered "dirty"
      totalPriceControl.markAsTouched();
      
      // Force change detection immediately in Angular zone
      this.ngZone.run(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        // Trigger change detection again to ensure warning message updates
        setTimeout(() => {
          this.cdr.markForCheck();
        }, 0);
      });
      
      console.log('Total price calculated:', { 
        index, 
        quantity: quantityNum, 
        unitPrice: unitPriceNum, 
        totalPrice,
        formValue: totalPriceControl.value,
        controlValue: priceGroup.get('totalPrice')?.value
      });
    } catch (error) {
      console.error('Error calculating total price:', error);
    }
  }

  switchTab(tab: 'specification' | 'quote') {
    this.activeTab = tab;
    
    // If switching to quote tab and quantities haven't been initialized, initialize them
    if (tab === 'quote' && !this.quantitiesInitialized && this.lead) {
      // Use setTimeout to ensure the tab is switched first
      setTimeout(() => {
        this.initializeQuantityPrices();
      }, 0);
    }
  }

  formatDate(date: string | Date): string {
    if (!date) return '-';
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  formatDateForInput(date: string | Date): string {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatSpecValue(value: string): string {
    if (!value) return '';
    // If it's a bullet point format (newlines), convert to HTML list
    if (value.includes('\n')) {
      const lines = value.split('\n').filter(line => line.trim());
      return '<ul>' + lines.map(line => `<li>${line.trim()}</li>`).join('') + '</ul>';
    }
    return value;
  }

  getSpecifications(): any[] {
    // Get specifications from product, productDetailsSnapshot, or lead's productDetailsSnapshot
    return this.product?.specifications || 
           this.product?.productDetailsSnapshot?.specifications || 
           this.lead?.productDetailsSnapshot?.specifications || 
           [];
  }

  getFileName(url: string): string {
    if (!url) return 'Document.pdf';
    const parts = url.split('/');
    return parts[parts.length - 1] || 'Document.pdf';
  }

  getFileSize(url: string): string {
    // This would typically come from the backend, but for now return a placeholder
    return '100 MB';
  }

  getShippingAddress(): string {
    // Check multiple sources for shipping details
    const shipping = this.product?.shippingDetails || 
                    this.product?.shippingAddress ||
                    this.product?.productDetailsSnapshot?.shippingDetails ||
                    this.lead?.productDetailsSnapshot?.shippingDetails;
    
    if (!shipping) return '-';
    
    const parts = [
      shipping.addressLine1,
      shipping.addressLine2,
      shipping.city,
      shipping.state,
      shipping.country || 'India',
      shipping.pincode ? `- ${shipping.pincode}` : ''
    ].filter(Boolean);
    
    return parts.join(', ');
  }

  getShippingTerm(): string {
    // Check multiple sources for shipping term
    const shipping = this.product?.shippingDetails || 
                    this.product?.shippingAddress ||
                    this.product?.shippingTerm ||
                    this.product?.productDetailsSnapshot?.shippingDetails ||
                    this.lead?.productDetailsSnapshot?.shippingDetails;
    
    return shipping?.shippingTerm?.toUpperCase() || '-';
  }

  getProductImageUrl(): string {
    // Check product.image first
    if (this.product?.image) {
      if (this.product.image.startsWith('http://') || this.product.image.startsWith('https://')) {
        return this.product.image;
      }
      return this.product.image;
    }
    
    // Check productDetailsSnapshot
    if (this.product?.productDetailsSnapshot?.image) {
      const img = this.product.productDetailsSnapshot.image;
      if (img.startsWith('http://') || img.startsWith('https://')) {
        return img;
      }
      return img;
    }
    
    // Check lead's productDetailsSnapshot
    if (this.lead?.productDetailsSnapshot?.image) {
      const img = this.lead.productDetailsSnapshot.image;
      if (img.startsWith('http://') || img.startsWith('https://')) {
        return img;
      }
      return img;
    }
    
    return '';
  }

  viewProductImage() {
    const imageUrl = this.getProductImageUrl();
    if (imageUrl) {
      this.modalImageUrl = imageUrl;
      // Extract filename from URL or use default
      this.modalImageName = this.extractFileNameFromUrl(imageUrl) || 'Product Image';
      this.showImageModal = true;
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
  }

  closeImageModal() {
    this.showImageModal = false;
    this.modalImageUrl = '';
    this.modalImageName = '';
    // Restore body scroll
    document.body.style.overflow = '';
  }

  downloadImage() {
    if (this.modalImageUrl) {
      const link = document.createElement('a');
      link.href = this.modalImageUrl;
      link.download = this.modalImageName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  extractFileNameFromUrl(url: string): string {
    try {
      // Extract filename from URL
      const urlParts = url.split('/');
      const fileName = urlParts[urlParts.length - 1].split('?')[0]; // Remove query params
      return fileName || 'image.jpg';
    } catch (error) {
      return 'image.jpg';
    }
  }

  // Check if all mandatory quantities have prices filled
  areMandatoryQuantitiesFilled(): boolean {
    if (!this.quoteForm || !this.quantityPrices) {
      return false;
    }

    const quantityPricesArray = this.quantityPrices;
    
    // If no quantities, return true (nothing to validate)
    if (quantityPricesArray.length === 0) {
      return true;
    }
    
    let hasMandatoryQuantity = false;
    
    for (let i = 0; i < quantityPricesArray.length; i++) {
      const priceGroup = quantityPricesArray.at(i);
      const isMandatory = priceGroup.get('isMandatory')?.value === true;
      const unitPrice = priceGroup.get('unitPrice')?.value;
      
      // If mandatory, check if unit price is filled and valid
      if (isMandatory) {
        hasMandatoryQuantity = true;
        if (!unitPrice || unitPrice === null || unitPrice === '' || unitPrice <= 0 || isNaN(unitPrice)) {
          console.log('Mandatory quantity missing price:', { index: i, isMandatory, unitPrice });
          return false;
        }
      }
    }
    
    // If no mandatory quantities exist, return true
    if (!hasMandatoryQuantity) {
      return true;
    }
    
    return true;
  }

  // Show warning message when mandatory quantities are not filled
  showMandatoryWarning(): boolean {
    if (!this.isQuoteEditable()) {
      return false;
    }
    
    if (!this.quantityPrices || this.quantityPrices.length === 0) {
      return false;
    }
    
    return !this.areMandatoryQuantitiesFilled();
  }

  submitQuote() {
    // Check if all mandatory quantities have prices
    if (!this.areMandatoryQuantitiesFilled()) {
      this.quoteForm.markAllAsTouched();
      this.showError('Please fill in the price for all mandatory quantities before submitting.');
      return;
    }

    if (this.quoteForm.invalid) {
      this.quoteForm.markAllAsTouched();
      this.showError('Please fill all required fields');
      return;
    }

    // Validate that at least one unit price is filled
    const hasPrice = this.quantityPrices.controls.some(control => {
      const unitPrice = control.get('unitPrice')?.value;
      return unitPrice && unitPrice > 0;
    });

    if (!hasPrice) {
      this.showError('Please enter at least one unit price');
      return;
    }

    // Show confirmation modal
    const quoteId = this.lead?.quoteId || 'N/A';
    this.confirmModalMessage = `Do you want to submit this Quote (${quoteId}) ?`;
    this.showConfirmModal = true;
  }

  onConfirmSubmit() {
    this.showConfirmModal = false;
    
    // Set submitting immediately to show loader
    this.submitting = true;
    this.errorMessage = '';
    this.successMessage = '';
    
    // Trigger change detection to update UI immediately
    this.cdr.detectChanges();
    
    // Small delay to ensure modal closes and UI updates
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 50);

    const formValue = this.quoteForm.getRawValue();
    const quantityPrices = formValue.quantityPrices.map((qp: any) => {
      // Convert empty/null/undefined unitPrice to null for non-mandatory fields
      let unitPrice = qp.unitPrice;
      if (unitPrice === null || unitPrice === undefined || unitPrice === '' || unitPrice === 0) {
        // Check if this quantity is mandatory
        const isMandatory = qp.isMandatory;
        // Only set to null if not mandatory; mandatory fields should have been validated already
        unitPrice = isMandatory ? (qp.unitPrice || null) : null;
      }
      
      // Calculate totalPrice if unitPrice is provided
      let totalPrice = qp.totalPrice || 0;
      if (unitPrice && qp.quantity) {
        totalPrice = Number(qp.quantity) * Number(unitPrice);
      }
      
      return {
        quantity: qp.quantity,
        uom: qp.uom,
        unitPrice: unitPrice,
        totalPrice: totalPrice,
        isMandatory: qp.isMandatory || false
      };
    });

    // Convert validityDate to ISO string if it's a date string
    let validityDateValue = formValue.validityDate;
    if (validityDateValue && typeof validityDateValue === 'string') {
      validityDateValue = new Date(validityDateValue).toISOString();
    }

    const updateData = {
      validityDate: validityDateValue,
      quantityPrices: quantityPrices,
      notes: formValue.notes || '',
      status: 'Active'
    };

    this.apiService.updateQuote(this.lead._id, updateData).subscribe({
      next: (response: ApiResponse) => {
        // Extract data and message from standardized response
        const responseData = extractData(response);
        const message = extractMessage(response) || 'Quote submitted successfully!';
        
        // Show success message first while loader is still running
        this.ngZone.run(() => {
          this.successMessage = message;
          this.cdr.detectChanges();
        });
        
        // Keep loader visible for a moment to ensure smooth transition
        // Then stop loader after success message is displayed
        setTimeout(() => {
          this.ngZone.run(() => {
          
            this.cdr.detectChanges();
          });
          
          // Auto-hide success message after 5 seconds
          setTimeout(() => {
            this.ngZone.run(() => {
              this.successMessage = '';
              this.cdr.detectChanges();
            });
          }, 5000);
          
          // Redirect to quotes page after 3 seconds
          setTimeout(() => {
            this.submitting = false;
            this.router.navigate(['/quote']);
          }, 3000);
        }, 500); // Delay to keep loader visible until success message appears
      },
      error: (err: ApiResponse) => {
        console.error('Error submitting quote:', err);
        // Extract error message from standardized response
        const errorMessage = extractErrorMessage(err) || 'Failed to submit quote. Please try again.';
        
        // Show error message first while loader is still running
        this.ngZone.run(() => {
          this.errorMessage = errorMessage;
          this.cdr.detectChanges();
        });
        
        // Keep loader visible for a moment to ensure smooth transition
        // Then stop loader after error message is displayed
        setTimeout(() => {
          this.ngZone.run(() => {
            this.submitting = false;
            this.cdr.detectChanges();
          });
          
          // Auto-hide error message after 5 seconds
          setTimeout(() => {
            this.ngZone.run(() => {
              this.errorMessage = '';
              this.cdr.detectChanges();
            });
          }, 5000);
        }, 500); // Delay to keep loader visible until error message appears
      }
    });
  }

  onCancelSubmit() {
    this.showConfirmModal = false;
  }

  closeConfirmModal() {
    this.showConfirmModal = false;
  }

  goBack() {
    // Check current route to determine where to go back
    const currentRoute = this.router.url;
    if (currentRoute.startsWith('/quote/view')) {
      // If coming from quotes section, go back to quotes
      this.router.navigate(['/quote']);
    } else {
      // Otherwise, go back to leads
      this.router.navigate(['/leads']);
    }
  }

  showError(message: string) {
    this.errorMessage = message;
    setTimeout(() => {
      this.errorMessage = '';
    }, 5000);
  }

  formatNumber(num: number): string {
    if (!num || isNaN(num)) return '0';
    return num.toLocaleString('en-IN');
  }

  getTotalPrice(priceGroup: any): number {
    const totalPrice = priceGroup?.get('totalPrice')?.value;
    if (totalPrice === null || totalPrice === undefined || totalPrice === '') {
      return 0;
    }
    const num = typeof totalPrice === 'string' ? parseFloat(totalPrice) : Number(totalPrice);
    return isNaN(num) ? 0 : num;
  }

  printPage() {
    // Add print class to body to trigger print styles
    document.body.classList.add('printing');
    
    // Wait a moment for styles to apply, then print
    setTimeout(() => {
      window.print();
      // Remove print class after printing
      setTimeout(() => {
        document.body.classList.remove('printing');
      }, 100);
    }, 100);
  }
crrntStatus:string=""
  getStatusClass() {
    
    if (!this.lead)  this.crrntStatus = 'status-pending';
    
    const status = (this.lead.status || '').trim();
    console.log(status)

    if (status === 'Expired') {
      this.crrntStatus = 'status-expired';
    }else if(status === 'Active') {
      this.crrntStatus = 'status-active';
    }else {
      // Default to pending for "Yet to Quote" and other statuses
      this.crrntStatus = 'status-pending';

    }
  }
 currentText:string=''
  getStatusText() {
    console.log('getStatusText', this.lead);
    // if (!this.lead) return 'Yet to Quote';

    const status = (this.lead.status || '').trim();
    
    // Return the actual status if it's one of the expected values
    if (status === 'Yet to Quote' || status === 'Expired' || status === 'Active' || status === 'Submitted') {
      // console.log('getStatusTextryt46', status);
      this.currentText = status;
    } else {

      
      // Default to "Yet to Quote" if status is empty or unknown
      this.currentText = 'Yet to Quote';
    }
  }

  isQuoteEditable(): boolean {
    if (!this.lead) return false;
    const status = (this.lead.status || '').trim();
    // Only editable when status is "Yet to Quote" AND coming from leads section
    return status === 'Yet to Quote' && this.isFromLeads;
  }

  getTodayDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  openDatePicker(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input && input.type === 'date' && this.isQuoteEditable()) {
      // Stop event propagation to prevent any parent handlers
      event.stopPropagation();
      // Try to open the date picker if the browser supports it
      if ('showPicker' in HTMLInputElement.prototype) {
        try {
          input.showPicker();
        } catch (err) {
          // If showPicker fails, just focus the input (browser will show picker on focus)
          input.focus();
        }
      } else {
        // Fallback: just focus the input
        input.focus();
      }
    }
  }

  preventManualDateInput(event: KeyboardEvent | Event) {
    if (!this.isQuoteEditable()) {
      if (event instanceof KeyboardEvent) {
        event.preventDefault();
      }
      return;
    }

    if (event instanceof KeyboardEvent) {
      const key = event.key;
      // Allow navigation and special keys
      const allowedKeys = [
        'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End', 'PageUp', 'PageDown',
        'Delete', 'Backspace'
      ];
      
      // Prevent Ctrl/Cmd combinations that could be used for typing
      if ((event.ctrlKey || event.metaKey) && (key === 'v' || key === 'V')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      
      // Prevent all character keys (manual typing)
      // Allow only navigation and special keys
      if (!allowedKeys.includes(key) && key.length === 1) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  viewSupplier(supplier: any) {
    // Navigate to supplier view details page
    const supplierId = supplier._id || supplier.id || '';
    this.router.navigate(['/supplier/view', supplierId]);
    // this.closeActionMenu();
  }

}
